import type { AdapterIoResultListApiData, AdapterReviewCaseListApiData, BookingStatus } from '@navio/nsdk';
import { type FilterUnit, filterUnitFor, type SharedQuery, useFilterUnits } from 'noddi-ui-web';
import { ADDRESS_FILTER_TEXT, useAddressOptions } from '../Address/useAddressFilter';
import { useAdapterLinkedBookingFilters } from '../Booking/useAdapterLinkedBookingFilters';
import { useInvoiceOptions } from '../Invoice/useInvoicesFilter';
import { useCityOptions } from '../Location/useCitiesFilter';
import { ORDER_STATUS_FILTER_OPTIONS, ORDER_TRANSACTION_STATUS_FILTER_OPTIONS } from '../Order/useOrderStatusFilters';
import { RECALL_TYPE_FILTER_OPTIONS } from '../Recall/useRecallTypeFilter';
import { USER_GROUP_FILTER_TEXT } from '../UserGroup/useUserGroupTableFilter';

type ListOnly = 'pageIndex' | 'pageSize' | 'ordering' | 'search';
type AdapterIoResultQuery = Omit<NonNullable<AdapterIoResultListApiData['query']>, ListOnly>;
type AdapterReviewCaseQuery = Omit<NonNullable<AdapterReviewCaseListApiData['query']>, ListOnly>;
/** Params both adapter lists (I/O results and review cases) and their filter counts accept. */
type AdapterLinkedQuery = SharedQuery<AdapterIoResultQuery, AdapterReviewCaseQuery>;

export const adapterLinkedFilterUnit = filterUnitFor<AdapterLinkedQuery>();

const customerText = USER_GROUP_FILTER_TEXT;

/** Customer, order and recall filters with static options, on the adapter endpoints' params. */
const STATIC_FILTERS = [
  adapterLinkedFilterUnit.multiSelect('phoneNumberTypes', { ...customerText.phoneNumberTypes, exclude: true }),
  adapterLinkedFilterUnit.multiSelect('phoneNumberCountries', { ...customerText.phoneNumberCountries, exclude: true }),
  adapterLinkedFilterUnit.boolean('hasCars', customerText.hasCars),
  adapterLinkedFilterUnit.boolean('hasTireInTireHotel', customerText.hasTireInTireHotel),
  adapterLinkedFilterUnit.boolean('hasDuplicatePhoneNumber', customerText.hasDuplicatePhoneNumber),
  adapterLinkedFilterUnit.numberRange('numCars', { ...customerText.numCars, exclude: false }),
  adapterLinkedFilterUnit.multiSelect('orderStatuses', {
    title: 'Order status',
    description: 'Filter orders by their current status (e.g., Pending, Completed, Cancelled)',
    options: ORDER_STATUS_FILTER_OPTIONS,
    exclude: true
  }),
  adapterLinkedFilterUnit.multiSelect('orderTransactionStatuses', {
    title: 'Transaction status',
    description: 'Filter by the payment transaction status',
    options: ORDER_TRANSACTION_STATUS_FILTER_OPTIONS,
    exclude: true
  })
] as const;

const RECALL_FILTERS = [
  adapterLinkedFilterUnit.multiSelect('recallTypes', {
    title: 'Recall type',
    description:
      'The type of recall proposal. Tire hotel recalls target customers with stored tires, wheel change recalls target customers due for a seasonal tire swap.',
    options: RECALL_TYPE_FILTER_OPTIONS,
    exclude: true
  }),
  adapterLinkedFilterUnit.boolean('invalidPhone', {
    title: 'Invalid phone',
    description: 'Filter on whether the phone number is missing, not mobile, or blocked from SMS.'
  }),
  adapterLinkedFilterUnit.boolean('hasMediumWrong', {
    title: 'Has medium wrong',
    description: 'Filter on whether the recall has a medium wrong flag.'
  })
] as const;

/** Partner booking/customer/order/recall filters applied to adapter lists. */
export const useAdapterLinkedDomainFilters = ({
  serviceDepartmentIds,
  baseExcludedStatusIds
}: {
  serviceDepartmentIds?: number[];
  baseExcludedStatusIds?: BookingStatus[];
} = {}) => {
  const bookingListFilters = useAdapterLinkedBookingFilters({ serviceDepartmentIds, baseExcludedStatusIds });
  /** Compile-time guard: every booking param the set writes must exist on both adapter endpoints. */
  const bookingFilters: FilterUnit<Pick<AdapterLinkedQuery, keyof typeof bookingListFilters.apiInput>> =
    bookingListFilters;
  const [phoneNumberTypes, phoneNumberCountries, ...customerFlags] = STATIC_FILTERS;

  return useFilterUnits([
    bookingFilters,
    phoneNumberTypes,
    phoneNumberCountries,
    adapterLinkedFilterUnit.multiSelect('cities', {
      ...customerText.cities,
      source: useCityOptions(),
      exclude: true
    }),
    ...customerFlags,
    adapterLinkedFilterUnit.multiSelect('invoiceIds', {
      title: 'Invoices',
      defaultVisibleOptions: 3,
      source: useInvoiceOptions({ serviceDepartmentIds }),
      exclude: true
    }),
    adapterLinkedFilterUnit.multiSelect('addressIds', {
      ...ADDRESS_FILTER_TEXT,
      source: useAddressOptions({ serviceDepartmentIds }),
      exclude: true
    }),
    ...RECALL_FILTERS
  ]);
};
